import { useEffect, useMemo, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { AxiosError } from 'axios'
import {
  deleteBasketItem,
  fetchBasket,
  saveBasketItem,
  updateBasketItem,
} from '../api/calculatorApi'
import type {
  BasketItem,
  BasketItemInput,
  BasketItemResponse,
  BasketResponse,
} from '../types'
import { CALCULATOR_KEYS } from './calculatorKeys'

/** Корзина персональная и меняется только этим экраном. */
export const useBasketQuery = (enabled = true) =>
  useQuery<BasketResponse, AxiosError>(CALCULATOR_KEYS.basket, fetchBasket, {
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  })

/**
 * Кладёт позицию в уже загруженный список вместо перезапроса.
 *
 * Если списка в кеше ещё нет — не создаём его из одной позиции: иначе первый
 * `GET /basket` под `staleTime` может и не случиться, и человек увидит корзину
 * из одного продукта вместо всех своих.
 */
const useUpsertBasketItem = () => {
  const queryClient = useQueryClient()
  return (item: BasketItem) =>
    queryClient.setQueryData<BasketResponse | undefined>(
      CALCULATOR_KEYS.basket,
      current => {
        if (!current) return current
        const index = current.items.findIndex(
          one => one.ingredient_id === item.ingredient_id
        )
        if (index === -1) return { items: [...current.items, item] }
        const items = [...current.items]
        items[index] = item
        return { items }
      }
    )
}

export interface BasketWriteInput extends BasketItemInput {
  /** Позиция уже в корзине — правим её, а не создаём заново. */
  exists: boolean
}

/**
 * Запись продукта в корзину.
 *
 * Новая позиция — POST, правка уже открытой — PUT: её могли удалить со второго
 * устройства, и воскрешать её молча неправильно. `404` от PUT означает ровно
 * это, поэтому перезапрашиваем корзину — пусть человек увидит её настоящее
 * состояние.
 *
 * Ответ содержит записанную позицию, поэтому список не перезапрашиваем.
 */
export const useWriteBasketItemMutation = () => {
  const queryClient = useQueryClient()
  const upsert = useUpsertBasketItem()

  return useMutation<BasketItemResponse, AxiosError, BasketWriteInput>(
    ({ exists, ...body }) =>
      exists ? updateBasketItem(body) : saveBasketItem(body),
    {
      retry: false,
      onSuccess: data => {
        upsert(data.item)
      },
      onError: error => {
        if (error.response?.status === 404) {
          void queryClient.invalidateQueries(CALCULATOR_KEYS.basket)
        }
      },
    }
  )
}

/**
 * Удаление продукта. Адресуется ингредиентом, не строкой корзины.
 *
 * `404` считаем успехом: позиции уже нет — ровно то состояние, которого мы и
 * добивались, показывать ошибку незачем.
 */
export const useDeleteBasketItemMutation = () => {
  const queryClient = useQueryClient()

  const drop = (ingredientId: number) =>
    queryClient.setQueryData<BasketResponse | undefined>(
      CALCULATOR_KEYS.basket,
      current =>
        current
          ? {
              items: current.items.filter(
                item => item.ingredient_id !== ingredientId
              ),
            }
          : current
    )

  return useMutation<void, AxiosError, number>(deleteBasketItem, {
    retry: false,
    onSuccess: (_data, ingredientId) => {
      drop(ingredientId)
    },
    onError: (error, ingredientId) => {
      if (error.response?.status === 404) drop(ingredientId)
    },
  })
}

/**
 * Перезапрашивает справочник, если встретился идентификатор, которого в нём
 * нет. Так велит контракт: удалённых позиций справочник не отдаёт, а новые
 * появляются в нём в пределах минуты — без перезапроса продукт остался бы
 * безымянным до перезагрузки экрана.
 */
export const useRefetchDictionaryOnUnknown = (
  unknownIds: readonly number[]
) => {
  const queryClient = useQueryClient()
  const asked = useRef('')

  const signature = useMemo(
    () => [...unknownIds].sort((a, b) => a - b).join(','),
    [unknownIds]
  )

  useEffect(() => {
    if (!signature || asked.current === signature) return
    asked.current = signature
    void queryClient.invalidateQueries(CALCULATOR_KEYS.dictionary)
  }, [signature, queryClient])
}
